import { ElasticColors, GuidelineCategories, PatientBriefActions, PatientBriefModalMode } from 'consts/hiToolsConsts'

// Where each auto-filled field lives in the brief form values
const FIELD_TARGETS = {
  attachments: { category: GuidelineCategories.AlignersTxTracking, item: PatientBriefActions.Attachments },
  untrackedTeeth: { category: GuidelineCategories.AlignersTxTracking, item: PatientBriefActions.UntrackedTeeth },
  iprAlignerNumber: { category: GuidelineCategories.IPR, item: PatientBriefActions.AlignerNumberReached },
  elastics: { category: GuidelineCategories.Elastics, item: PatientBriefActions.Elastics }
}

const TEETH_FIELDS = ['attachments', 'untrackedTeeth']

// The IPR field stores what was typed (a string); the model returns a number. Compare them as numbers.
const toAlignerNumber = value => (value === '' || value === null || value === undefined ? NaN : Number(value))

const hasValue = (fieldName, value) => {
  if (fieldName === 'iprAlignerNumber') {
    const alignerNumber = toAlignerNumber(value)
    return Number.isInteger(alignerNumber) && alignerNumber > 0
  }
  return Array.isArray(value) && value.length > 0
}

const toItemValue = (fieldName, value) => {
  if (fieldName === 'elastics') {
    return value.map((elastic, index) => ({
      name: `Elastic ${index + 1}`,
      color: ElasticColors[index % ElasticColors.length],
      selections: { ...elastic.selections }
    }))
  }
  if (fieldName === 'iprAlignerNumber') {
    return String(value)
  }
  return Array.isArray(value) ? [...value] : value
}

export const getExtractedAutofillFields = (fields = {}) =>
  Object.keys(FIELD_TARGETS).filter(fieldName => hasValue(fieldName, fields?.[fieldName]))

export const getAppliedAutofillFields = (values, fields) =>
  Object.keys(FIELD_TARGETS).filter(
    fieldName => values.categories?.[FIELD_TARGETS[fieldName].category] && hasValue(fieldName, fields?.[fieldName])
  )

export const applyBriefAutofill = (values, fields) => {
  const appliedFields = getAppliedAutofillFields(values, fields)
  if (!appliedFields.length) {
    return values
  }
  const categories = { ...values.categories }
  appliedFields.forEach(fieldName => {
    const { category, item } = FIELD_TARGETS[fieldName]
    categories[category] = {
      ...categories[category],
      isEnabled: true,
      items: { ...categories[category].items, [item]: toItemValue(fieldName, fields[fieldName]) }
    }
  })
  return { ...values, categories }
}

const readField = (values, fieldName) => {
  const { category, item } = FIELD_TARGETS[fieldName]
  const categoryValues = values.categories?.[category]
  if (!categoryValues?.isEnabled) {
    return fieldName === 'iprAlignerNumber' ? null : []
  }
  const value = categoryValues.items?.[item]
  return fieldName === 'iprAlignerNumber' ? value ?? null : value || []
}

export const takeAutofillSnapshot = (values, fieldNames) =>
  Object.fromEntries(fieldNames.map(fieldName => [fieldName, readField(values, fieldName)]))

const comparableValue = (fieldName, value) => {
  if (fieldName === 'elastics') {
    return JSON.stringify(value.map(elastic => elastic.selections))
  }
  return fieldName === 'iprAlignerNumber' ? String(toAlignerNumber(value)) : JSON.stringify(value)
}

export const diffBriefAutofill = (snapshot, submittedValues) => {
  const diff = { teethAdded: 0, teethRemoved: 0 }
  Object.keys(snapshot).forEach(fieldName => {
    const filled = snapshot[fieldName]
    const submitted = readField(submittedValues, fieldName)
    if (!hasValue(fieldName, submitted)) {
      diff[fieldName] = 'cleared'
    } else {
      diff[fieldName] =
        comparableValue(fieldName, filled) === comparableValue(fieldName, submitted) ? 'kept' : 'changed'
    }
    if (TEETH_FIELDS.includes(fieldName)) {
      diff.teethAdded += submitted.filter(tooth => !filled.includes(tooth)).length
      diff.teethRemoved += filled.filter(tooth => !submitted.includes(tooth)).length
    }
  })
  return diff
}

// Hidden while practice guidelines load: the reload rebuilds the form and would wipe an auto-fill.
// Hidden for a new patient without guidelines: that modal shows only the empty state, with no form to fill.
export const isBriefAutofillAvailable = ({
  isFlagOn,
  mode,
  isMobile,
  isLoadingPracticeGuidelines,
  newPatientWithoutGuidelines
}) =>
  Boolean(isFlagOn) &&
  mode !== PatientBriefModalMode.View &&
  !isMobile &&
  !isLoadingPracticeGuidelines &&
  !newPatientWithoutGuidelines

// Prefixed to the feedback text so the team can find the analysis (the analysisId matches the service log)
export const buildAutofillFeedbackContext = ({ patientId, mode, result = {} }) =>
  [
    'Patient brief AI auto-fill',
    `patientId: ${patientId}`,
    `mode: ${mode}`,
    result.analysisId && `analysisId: ${result.analysisId}`,
    result.status && `status: ${result.status}`,
    result.failureReason && `failureReason: ${result.failureReason}`,
    result.filledFields?.length && `filled: ${result.filledFields.join(', ')}`
  ]
    .filter(Boolean)
    .join(' | ')
